<script lang="ts" setup>
import { AddressBookBundle, useAddressBookStore } from '@/store/addressBook';
import makeBlockie from 'ethereum-blockies-base64';
import AddressBookAddressesTable from '@/components/organisms/AddressBookAddressesTable.vue';
import { useRouter } from 'vue-router';
import { snackbarPush } from '@/helpers/snackbarPush';
import Export from './Export.vue';
import Import from './Import.vue';

const router = useRouter();
const store = useAddressBookStore();

function share(bundle: AddressBookBundle) {
  const { href } = router.resolve({
    name: 'Portfolio',
    params: {
      addresses: bundle.addresses,
      bundleId: bundle.id,
    },
  });
  if (navigator.share) {
    navigator
      .share({
        title: `Jupiter Portfolio | Bundle`,
        url: href,
      })
      .catch(() => {});
  } else {
    navigator.clipboard.writeText(`${window.location.origin}${href}`);
    snackbarPush('Copied to clipboard.');
  }
}
function pin(bundle: AddressBookBundle) {
  store.pinBundle(bundle.id, !bundle.pinned);
}
</script>

<template>
  <v-container class="mb-12 flex flex-col gap-6">
    <header class="flex justify-between items-center py-4">
      <h1 class="text-3xl font-bold">Address Book</h1>
      <div class="flex gap-2">
        <Export />
        <Import />
      </div>
    </header>
    <section>
      <h2 class="mb-2 text-xl font-bold">Wallets</h2>
      <p class="text-subtitle-2 mb-3 text-medium-emphasis">
        Save addresses for quick access.
      </p>
      <AddressBookAddressesTable mode="items"></AddressBookAddressesTable>
    </section>
    <section>
      <h2 class="mb-2 mt-8 text-xl font-bold">Bundles</h2>
      <p class="text-subtitle-2 mb-3 text-medium-emphasis">
        Watch multiple addresses on one page.
      </p>
      <v-row>
        <v-col
          cols="12"
          sm="6"
          md="4"
          v-for="bundle in store.bundles"
          :key="bundle.name"
        >
          <v-card min-height="124px">
            <div style="position: absolute; right: 0">
              <v-btn
                icon
                size="x-small"
                variant="plain"
                @click.prevent="store.openBundleDialog(bundle.id)"
              >
                <v-icon size="large" icon="mdi-pencil"></v-icon>
              </v-btn>
              <v-btn
                icon
                size="x-small"
                variant="plain"
                :title="
                  bundle.pinned
                    ? 'Remove from Quick Access'
                    : 'Add to Quick Access'
                "
                @click.prevent="pin(bundle)"
              >
                <v-icon
                  size="large"
                  :icon="bundle.pinned ? 'mdi-pin' : 'mdi-pin-outline'"
                ></v-icon>
              </v-btn>
              <v-menu
                location="bottom right"
                offset="0 -10"
                :transition="false"
              >
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    size="small"
                    variant="plain"
                    icon="mdi-dots-vertical"
                  />
                </template>
                <v-card min-width="200px">
                  <v-list density="compact">
                    <v-list-item
                      prepend-icon="mdi-eye"
                      title="View"
                      link
                      :to="{
                        name: 'Portfolio',
                        params: {
                          addresses: bundle.addresses,
                          bundleId: bundle.id,
                        },
                      }"
                    />
                    <v-list-item
                      prepend-icon="mdi-share"
                      title="Share"
                      @click="share(bundle)"
                    />
                    <v-divider></v-divider>
                    <v-list-item
                      prepend-icon="mdi-delete"
                      title="Delete"
                      @click.prevent="store.removeBundle(bundle.id)"
                    />
                  </v-list>
                </v-card>
              </v-menu>
            </div>
            <v-card-title class="text-center mx-10">
              {{ bundle.name }}
            </v-card-title>
            <v-card-text>
              <div class="d-flex justify-center">
                <v-avatar
                  v-for="item in bundle.items.slice(0, 6)"
                  :key="item.address"
                  rounded
                  :image="makeBlockie(item.address)"
                  class="mx-1"
                  size="small"
                ></v-avatar>
                <div
                  v-if="bundle.items.length > 6"
                  class="ml-1 align-self-center text-medium-emphasis text-caption"
                >
                  +{{ bundle.items.length - 6 }}
                </div>
              </div>
              <v-card-subtitle class="text-center mt-2">
                {{ bundle.items.length }}
                {{ bundle.items.length > 1 ? 'addresses' : 'address' }}
              </v-card-subtitle>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-card
            @click="store.openBundleDialog()"
            min-height="124px"
            class="d-flex justify-center align-center"
          >
            <div>
              <v-btn icon size="x-large" variant="plain" disabled>
                <v-icon size="x-large" icon="mdi-plus"></v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </section>
  </v-container>
</template>
